import StepWizard, { type StepDefinition } from "./StepWizard";

export interface NewDealAnswers {
  국적: string;
  나이: string;
  군필: boolean;
  근로: string;
  사업자: string;
  학적: string;
  구직등록: string;
  타훈련: string;
  이력: string;
  수급: string;
}

interface NewDealFormProps {
  answers: NewDealAnswers;
  onChange: (answers: NewDealAnswers) => void;
  onSubmit: () => void;
}

const newDealSteps: StepDefinition[] = [
  {
    key: "국적",
    type: "radio",
    legend: "1. 국적",
    description: "참여제한 ⑤ — 대한민국 국적 보유가 요건입니다.",
    options: [
      { value: "한국", label: "대한민국 국적입니다" },
      { value: "외국", label: "대한민국 국적이 아닙니다" },
    ],
  },
  {
    key: "나이",
    type: "input",
    legend: "2. 나이",
    description: "참여 신청일 현재의 만 나이로 적어 주세요.",
    inputLabel: "만 나이",
    inputMin: 10,
    inputMax: 99,
    inputPlaceholder: "예: 27",
    checkbox: {
      key: "군필",
      label: "군 의무복무를 마쳤습니다",
      sub: "의무복무 기간만큼 연령 상한이 올라갑니다(최대 만 39세).",
    },
  },
  {
    key: "근로",
    type: "radio",
    legend: "3. 지금 일하고 계신가요",
    description: (
      <>
        취업 여부는 <b>신청일 현재 고용보험 피보험자격</b>으로 판단합니다.
      </>
    ),
    options: [
      { value: "미취업", label: "일하고 있지 않습니다" },
      {
        value: "프리랜서",
        label: "프리랜서입니다(3.3% 원천징수, 4대보험 미가입)",
      },
      {
        value: "단시간",
        label: "주 30시간 미만 단시간 근로 중입니다",
        sub: "아르바이트 등",
      },
      {
        value: "플랫폼",
        label: "플랫폼 노동·건설/일반 일용직입니다",
        sub: "배달, 대리, 일용 등",
      },
      {
        value: "재직",
        label: "주 30시간 이상 일하며 고용보험에 가입돼 있습니다",
      },
    ],
  },
  {
    key: "사업자",
    type: "radio",
    legend: "4. 사업자등록",
    options: [
      { value: "없음", label: "없습니다" },
      { value: "휴면", label: "등록은 있지만 실제로 사업을 하고 있지 않습니다" },
      { value: "영업", label: "등록이 있고 실제로 영업 중입니다" },
    ],
  },
  {
    key: "학적",
    type: "radio",
    legend: "5. 학적",
    description: "여기서 판정이 가장 많이 갈립니다. 해당하는 것 하나를 골라 주세요.",
    options: [
      { value: "없음", label: "학생이 아닙니다" },
      {
        value: "졸업예정",
        label: "대학(원) 4학년 2학기 이상 또는 마지막 학기 등록자입니다",
      },
      { value: "휴학", label: "휴학 중입니다" },
      { value: "학점충족", label: "졸업 이수학점을 모두 취득했습니다" },
      { value: "재학", label: "대학(원)에 재학 중입니다(위에 해당 없음)" },
      { value: "사이버대", label: "사이버대·방송통신대에 재학 중입니다" },
      { value: "학점은행", label: "학점은행제로 공부하고 있습니다" },
      {
        value: "고교직업",
        label: "특성화고·마이스터고 또는 일반고 직업교육학과 3학년 2학기입니다",
      },
      { value: "고교일반", label: "그 밖의 고등학교 재학생입니다" },
    ],
  },
  {
    key: "구직등록",
    type: "radio",
    legend: "6. 고용24(워크넷) 구직등록",
    description:
      "참여제한 ③ — 직업안정기관 구직등록이 참여 요건입니다. 온라인으로 즉시, 무료입니다.",
    options: [
      { value: "함", label: "했습니다" },
      { value: "안함", label: "안 했거나 잘 모르겠습니다" },
    ],
  },
  {
    key: "타훈련",
    type: "radio",
    legend: "7. 지금 다른 국비 훈련을 듣고 계신가요",
    description:
      "국가·지자체가 실시하거나 비용을 지원하는 직업능력개발훈련을 말합니다.",
    options: [
      { value: "없음", label: "아니요" },
      {
        value: "개강전종료",
        label: "듣고 있지만 아카데미 개시일 전에 끝납니다",
      },
      {
        value: "중복",
        label: "듣고 있고 아카데미 기간과 겹칩니다",
      },
    ],
  },
  {
    key: "이력",
    type: "radio",
    legend: "8. 올해 K-뉴딜 아카데미 참여 이력",
    description: (
      <>
        아카데미 <b>개시일 기준 연도</b>로 셉니다.
      </>
    ),
    options: [
      { value: "없음", label: "없습니다" },
      { value: "1회", label: "올해 한 번 참여했습니다(다른 아카데미)" },
      { value: "2회", label: "올해 두 번 이상 참여했습니다" },
      {
        value: "동일재참여",
        label: "전에 참여했던 것과 같은 아카데미에 다시 지원하려 합니다",
      },
      { value: "동시", label: "지금 다른 아카데미에 참여 중입니다" },
    ],
  },
  {
    key: "수급",
    type: "radio",
    legend: "9. 다른 지원제도 수급 (선택)",
    description:
      "자격 자체와는 무관합니다. 수당이 겹치는지 미리 짚어 드리기 위한 항목입니다.",
    optional: true,
    options: [
      { value: "없음", label: "해당 없음" },
      { value: "실업급여", label: "구직급여(실업급여)를 받고 있습니다" },
      { value: "국취제", label: "국민취업지원제도에 참여 중입니다" },
    ],
  },
];

export default function NewDealForm({ answers, onChange, onSubmit }: NewDealFormProps) {
  const firstUnanswered = newDealSteps.findIndex(
    (s) => !answers[s.key as keyof NewDealAnswers]
  );
  const initialIndex = firstUnanswered === -1 ? newDealSteps.length - 1 : firstUnanswered;

  return (
    <StepWizard
      steps={newDealSteps}
      answers={answers as unknown as Record<string, string | boolean>}
      onFieldChange={(key, value) =>
        onChange({ ...answers, [key]: value } as NewDealAnswers)
      }
      onComplete={onSubmit}
      initialIndex={initialIndex}
    />
  );
}